/* eslint-disable max-statements */
// @ts-expect-error TS(2305): Module '"prop-types"' has no exported member 'Prop... Remove this comment to see the full error message
import { PropTypes } from 'prop-types';
import type { Marker, Popup } from 'maplibre-gl';
import * as maplibregl from 'maplibre-gl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { Itinerary as ItineraryModule, Step, TravelMode } from '@wemap/routers';
import { core, CoreConfig } from '@wemap/core';
import '../src/maplibreSetup';
import { WemapMap } from '@wemap/map';
import LevelSwitcher from '../src/LevelSwitcher';
import 'maplibre-gl/dist/maplibre-gl.css';
import InstructionManager from './instruction-manager';
import { Accordion, AccordionItem } from './Accordion.tsx';
import { VStack, styled } from '../styled-system/jsx';

import type { LatLngLevel } from './types';
import { parseLatLngLevelParam } from './types';

import GraphHandler from './graph-handler.js';
import StepsHandler from './steps-handler.js';
import UserPositionHandler from './userposition-handler.js';
import ItineraryHandler from './itinerary-handler.js';
import PinpointsHandler from './pinpoints-handler.js';
import VpsHandler from './vps-handler.js';
import BuildingsHandler from './buildings-handler.js';
import toast from 'react-hot-toast';
import MapManager from './MapManager.tsx';

import { MULTIROUTERS_ENDPOINT, MULTIROUTERS_ENDPOINT_DEV } from './config.js';
import wheelchairDesignatedSvg from './img/wheelchair-designated.svg?raw';
import wheelchairNoSvg from './img/wheelchair-no.svg?raw';
import roadbookSvg from './img/roadbook.svg?raw';
import greenMarkerSvg from './img/marker-green.svg?raw';
import redMarkerSvg from './img/marker-red.svg?raw';
import durationSvg from './img/duration.svg?raw';
import distanceSvg from './img/distance.svg?raw';
import turnRightSvg from './img/turn_right.svg?raw';
import turnLeftSvg from './img/turn_left.svg?raw';
import turnSharpRightSvg from './img/turn_sharp_right.svg?raw';
import turnSharpLeftSvg from './img/turn_sharp_left.svg?raw';
import turnSlightRightSvg from './img/turn_slight_right.svg?raw';
import turnSlightLeftSvg from './img/turn_slight_left.svg?raw';
import straightSvg from './img/straight.svg?raw';
import directionsWalkSvg from './img/directions_walk.svg?raw';
import arrowUpSvg from './img/arrow_up.svg?raw';
import arrowDownSvg from './img/arrow_down.svg?raw';
import stairsSvg from './img/stairs.svg?raw';
import elevatorSvg from './img/elevator.svg?raw';
import escalatorSvg from './img/escalator.svg?raw';
import visuallyImpairedIcon from './img/visually-impaired.png';

const CheckboxLabel = styled('label', {
    base: {
        display: 'flex!',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        padding: '2px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        '&:hover': {
            backgroundColor: 'main/10',
        }
    }
});

const Checkbox = styled('input', {
    base: {
        width: '16px',
        height: '16px',
        cursor: 'pointer',
        accentColor: 'main',
    }
});

const OptionText = styled('span', {
    base: {
        fontSize: '10px',
        color: 'main',
        fontWeight: 'bold',
        userSelect: 'none'
    }
});

const LegendContainer = styled('div', {
    base: {
        marginTop: '0px',
        marginLeft: '24px',
        padding: '8px',
        backgroundColor: 'gray.100',
        borderRadius: '4px',
        fontSize: '12px'
    }
});

const LegendItem = styled('div', {
    base: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '4px',
        '&:last-child': {
            marginBottom: '0'
        }
    }
});

const LegendColor = styled('div', {
    base: {
        width: '16px',
        height: '2px',
        borderRadius: '1px'
    }
});

const LegendText = styled('span', {
    base: {
        fontSize: '12px',
        color: 'gray.700'
    }
});

const InfoPanel = styled('div', {
    base: {
        position: 'absolute',
        top: '20px',
        right: '20px',
        backgroundColor: 'white',
        borderRadius: '16px',
        zIndex: '10',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        minWidth: '320px',
        maxWidth: '90vw',
        maxHeight: '70vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
    }
});

const PanelHeader = styled('div', {
    base: {
        padding: '16px 20px',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }
});

const HeaderInfo = styled('div', {
    base: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: 1
    }
});

const MainInfo = styled('div', {
    base: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '12px'
    }
});

const IconWrapper = styled('span', {
    base: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '20px',
        height: '20px',
        marginRight: '8px',
        flexShrink: 0,
        '& svg': {
            width: '100%',
            height: '100%',
            fill: 'currentColor'
        }
    }
});

const InfoRow = styled('div', {
    base: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    }
});

const DistanceText = styled('span', {
    base: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#1a1a1a',
        display: 'flex',
        alignItems: 'center'
    }
});

const TimeText = styled('span', {
    base: {
        fontSize: '16px',
        color: '#5f6368',
        fontWeight: '400',
        display: 'flex',
        alignItems: 'center'
    }
});

const CloseButton = styled('button', {
    base: {
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: '8px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color 0.2s',
        '&:hover': {
            backgroundColor: '#f5f5f5'
        },
        '& svg': {
            width: '20px',
            height: '20px',
            fill: '#5f6368'
        }
    }
});

const StepsSection = styled('div', {
    base: {
        borderTop: '1px solid #e0e0e0'
    }
});

const StepsToggle = styled('button', {
    base: {
        width: '100%',
        padding: '12px 20px',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'background-color 0.2s',
        '&:hover': {
            backgroundColor: '#f5f5f5'
        }
    }
});

const StepsCount = styled('span', {
    base: {
        fontSize: '14px',
        color: '#5f6368',
        fontWeight: '500'
    }
});

const ExpandIcon = styled('div', {
    base: {
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s',
        '&.expanded': {
            transform: 'rotate(180deg)'
        },
        '& svg': {
            width: '16px',
            height: '16px',
            fill: '#5f6368'
        }
    }
});

const StepsList = styled('div', {
    base: {
        maxHeight: '400px',
        overflowY: 'auto',
        padding: '8px 0'
    }
});


const StepItem = styled('div', {
    base: {
        padding: '12px 20px',
        display: 'flex',
        flexDirection: 'column',
        transition: 'background-color 0.2s',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#f5f5f5'
        },
        '&.selected': {
            backgroundColor: '#e8f0fe'
        }
    }
});

const StepInstruction = styled('div', {
    base: {
        fontSize: '14px',
        color: '#1a1a1a',
        fontWeight: '400',
        position: 'relative',
        paddingRight: '32px',
        maxWidth: '240px',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: '1.4'
    }
});

const StepIcon = styled('div', {
    base: {
        position: 'absolute',
        right: '0',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '20px',
        height: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#2196F3',
        '& svg': {
            width: '100%',
            height: '100%',
            fill: 'currentColor'
        },
        '& path': {
            fill: 'currentColor'
        },
        '&.destination': {
            height: '25px',
            '& path': {
                fill: '#EF4444 !important'
            },
            '& circle': {
                fill: 'white !important'
            }
        }
    }
});

const StepSeparator = styled('div', {
    base: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 0',
        fontSize: '12px',
        color: '#5f6368',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '0',
            right: '0',
            top: '50%',
            height: '1px',
            backgroundColor: '#e0e0e0',
            zIndex: 0
        }
    },
    variants: {
        isLevelChange: {
            true: {
                padding: '12px 0',
                marginBottom: '0',
                '&::before': {
                    height: '3px',
                    backgroundColor: '#0F7FED',
                }
            }
        }
    }
});

const StepSeparatorText = styled('span', {
    base: {
        backgroundColor: 'white',
        padding: '0 8px',
        position: 'relative',
        zIndex: 1
    }
});

const StepSeparatorContainer = styled('div', {
    base: {
        position: 'relative',
        paddingLeft: '20px',
        paddingRight: '20px'
    },
    variants: {
        isLevelChange: {
            true: {
                marginBottom: '0',
                paddingBottom: '0'
            }
        }
    }
});

const LevelLabel = styled('div', {
    base: {
        fontSize: '11px',
        color: '#0F7FED',
        marginTop: '-12px',
        marginBottom: '8px',
        fontWeight: '500'
    }
});

const RoutePanel = styled('div', {
    base: {
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: 'white',
        borderRadius: '16px',
        zIndex: '10',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        width: '340px',
        maxWidth: '90vw',
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        boxSizing: 'border-box'
    }
});

const RouteSection = styled('div', {
    base: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    }
});

const RouteLabel = styled('div', {
    base: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#1a1a1a',
        marginBottom: '4px'
    }
});

const RouteInput = styled('input', {
    base: {
        flex: 1,
        padding: '6px 10px',
        fontSize: '12px !important',
        color: '#5f6368',
        backgroundColor: '#f5f5f5',
        border: '1px solid #e0e0e0',
        borderRadius: '6px',
        fontFamily: 'monospace',
        cursor: 'not-allowed',
        lineHeight: '1.4'
    }
});

const RouteInputRow = styled('div', {
    base: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    }
});

const RouteInputLabel = styled('span', {
    base: {
        fontSize: '13px',
        fontWeight: '500',
        color: '#1a1a1a',
        minWidth: '40px',
        lineHeight: '1.4'
    }
});

const RouteMarkerIcon = styled('div', {
    base: {
        width: '20px',
        height: '25px',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
});

const RulesContainer = styled('div', {
    base: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: '4px',
        maxWidth: '100%'
    }
});

const RulesDropdown = styled('select', {
    base: {
        padding: '6px 8px',
        borderRadius: '4px',
        border: '1px solid #ccc',
        fontSize: '12px !important',
        cursor: 'pointer',
        backgroundColor: 'white',
        '&:hover': {
            borderColor: '#999'
        }
    }
});

const RulesTagsContainer = styled('div', {
    base: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        alignItems: 'center',
        maxWidth: '100%'
    }
});

const RuleTag = styled('div', {
    base: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 8px',
        backgroundColor: '#e3f2fd',
        borderRadius: '4px',
        fontSize: '12px',
        color: '#1976d2',
        cursor: 'default',
        '&:hover .rule-tag-remove': {
            opacity: '1'
        }
    }
});

const RuleTagRemove = styled('span', {
    base: {
        cursor: 'pointer',
        opacity: '0.6',
        fontSize: '14px',
        fontWeight: 'bold',
        lineHeight: '1',
        '&:hover': {
            opacity: '1',
            color: '#d32f2f'
        }
    }
});

const InfoIcon = styled('span', {
    base: {
        fontSize: '12px',
        color: 'gray.500',
        cursor: 'help',
        marginLeft: '4px',
        fontWeight: 'bold',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        border: '1px solid',
        borderColor: 'gray.400',
        position: 'relative',
        '&:hover': {
            backgroundColor: 'gray.100',
            borderColor: 'gray.600'
        },
        '& .tooltip-text': {
            visibility: 'hidden',
            width: '200px',
            backgroundColor: 'black',
            color: 'white',
            textAlign: 'center',
            borderRadius: '6px',
            padding: '5px',
            position: 'absolute',
            zIndex: 1000,
            bottom: '125%',
            left: '50%',
            marginLeft: '-100px',
            fontSize: '11px',
            fontWeight: 'normal'
        },
        '&:hover .tooltip-text': {
            visibility: 'visible'
        },
        '& .tooltip-text::after': {
            content: '""',
            position: 'absolute',
            top: '100%',
            left: '50%',
            marginLeft: '-5px',
            borderWidth: '5px',
            borderStyle: 'solid',
            borderColor: 'black transparent transparent transparent'
        }
    }
});

const initializeMap = async (
    mapContainer: HTMLDivElement,
    emmid: string,
    token = ''
) => {
    await core.init({
        emmid,
        token,
    });

    const wemapMap = new WemapMap({
        container: mapContainer,
        maxZoom: 24,
        autoResize: true,
    });

    await wemapMap.whenReady();

    const emmidNumber = Number(CoreConfig.getConfig().emmid);

    return { wemapMap, emmid: emmidNumber };
};

const computeItinerary = async (
    emmid: number,
    start: any,
    end: any,
    options: { itineraryRules: string[], multiRoutersEndpoint: string, travelMode: TravelMode }
) => {
    const conv = (mb: LatLngLevel) => ({
        lat: mb.lat,
        lng: mb.lng,
        level: mb.level ?? undefined
    });

    const routerRequest: any = {
        origin: conv(start),
        destination: conv(end),
        travelMode: options.travelMode,
        mapId: emmid,
    };

    if (options.itineraryRules && options.itineraryRules.length > 0) {
        routerRequest.itineraryRules = options.itineraryRules;
    }

    const routerResponse = await fetch(`${options.multiRoutersEndpoint}/directions`, {
        method: 'POST',
        body: JSON.stringify(routerRequest),
        headers: {
            'Content-Type': 'application/json'
        }
    }).then(res => res.json());

    const itineraries = routerResponse.itineraries.map((itineraryJson: any) => {
        return ItineraryModule.fromJson(itineraryJson);
    });

    if (!itineraries.length) {
        toast.error('No route found.');
        return null;
    }

    return itineraries[0];
};

function MapPopupContents({
    onStartClick,
    onStartOutdoorClick,
    onEndClick,
    onEndOutdoorClick,
    onUserPositionClick,
    onStartMultipleItineraries,
    askUserPosition,
    level = null
}: any) {
    return (
        <div>
            <div onClick={onStartClick}
                className="itinerary-button">
                Select as <strong>start {level !== null ? `level ${level}` : ''}</strong>
            </div>
            <div onClick={onStartOutdoorClick}
                className="itinerary-button">
                Select as <strong>start outdoor</strong>
            </div>
            <div onClick={onEndClick}
                className="itinerary-button">
                Select as <strong>end {level !== null ? `level ${level}` : ''}</strong>
            </div>
            <div onClick={onEndOutdoorClick}
                className="itinerary-button">
                Select as <strong>end outdoor</strong>
            </div>
            {askUserPosition && (<div onClick={onUserPositionClick}
                className="itinerary-button">
                Fake <strong>user position</strong> here
            </div>)}
            <div onClick={onStartMultipleItineraries}
                className="itinerary-button">
                Check itineraries from here
            </div>
        </div>
    );
}

MapPopupContents.propTypes = {
    askUserPosition: PropTypes.bool,
    onStartClick: PropTypes.func,
    onEndClick: PropTypes.func,
    onUserPositionClick: PropTypes.func,
    onStartMultipleItineraries: PropTypes.func
};

function getMapLevel(wemapMap: WemapMap | null | undefined) {
    const level = wemapMap?.getLevel();
    return typeof level === 'number' ? level : null;
}

function formatDistance(meters: number): string {
    if (meters < 1000) {
        return `${Math.round(meters)}m`;
    }
    const km = Math.floor(meters / 1000);
    const remainingMeters = Math.round(meters % 1000);
    if (remainingMeters === 0) {
        return `${km}km`;
    }
    return `${km}km${remainingMeters}`;
}

function formatTime(seconds: number): string {
    const roundedSeconds = Math.round(seconds);
    if (roundedSeconds < 60) {
        return `${roundedSeconds}s`;
    }
    const hours = Math.floor(roundedSeconds / 3600);
    const minutes = Math.floor((roundedSeconds % 3600) / 60);
    const secs = roundedSeconds % 60;
    
    if (hours > 0) {
        if (minutes === 0 && secs === 0) {
            return `${hours}h`;
        }
        if (secs === 0) {
            return `${hours}h${minutes}min`;
        }
        return `${hours}h${minutes}min${secs}s`;
    }
    if (secs === 0) {
        return `${minutes}min`;
    }
    return `${minutes}min${secs}s`;
}

function formatCoordinate(pos: LatLngLevel | null): string {
    if (!pos) return '';
    const lat = pos.lat.toFixed(8);
    const lng = pos.lng.toFixed(8);
    const level = pos.level != null ? `, level ${pos.level}` : '';
    return `${lat}, ${lng}${level}`;
}

function getInstructionIcon(step: any): string | null {
    // Get direction from InstructionManager
    const { direction } = InstructionManager.getInstructionDetails(step);
    
    // Handle level changes with specific icons
    if (step.type === 'stairs') {
        return stairsSvg;
    }
    if (step.type === 'elevator') {
        return elevatorSvg;
    }
    if (step.type === 'escalator') {
        return escalatorSvg;
    }
    
    // Gate and subway-entrance have no icons
    if (step.type === 'gate' || step.type === 'subway-entrance') {
        return null;
    }
    
    // Handle directions
    switch (direction) {
        case 'right':
            return turnRightSvg;
        case 'left':
            return turnLeftSvg;
        case 'sharp-right':
            return turnSharpRightSvg;
        case 'sharp-left':
            return turnSharpLeftSvg;
        case 'slight-right':
            return turnSlightRightSvg;
        case 'slight-left':
            return turnSlightLeftSvg;
        case 'straight':
            return straightSvg;
        case 'up':
            return arrowUpSvg;
        case 'down':
            return arrowDownSvg;
        case 'actual-location':
            return directionsWalkSvg;
        default:
            return null;
    }
}


const markersColor = { start: '#22C55E', end: '#EF4444' } as const;
const FILTERED_RULES = ['wheelchair'] as const; // Rules handled via checkboxes, excluded from dropdown and tags

export default function Itinerary({
    emmid,
    token,
    graph,
    useGraphManagement,
    vpsMap,
    environment
}: any) {
    const [mapReady, setMapReady] = useState(false);
    const [start, setStart] = useState<LatLngLevel | null>(null);
    const [end, setEnd] = useState<LatLngLevel | null>(null);
    const [selectedRules, setSelectedRules] = useState<string[]>([]);
    const [travelMode, setTravelMode] = useState<TravelMode>('WALK');
    const [availableRules, setAvailableRules] = useState<string[]>([]);
    const [userStepInstruction, setUserStepInstruction] = useState(null);
    const [mapManager, setMapManager] = useState<MapManager | null>(null);
    const [showPinpointsProjection, setShowPinpointsProjection] = useState(false);
    const [showGraph, setShowGraph] = useState(true);
    const [showItinerary, setShowItinerary] = useState(true);
    const [showVpsLevels, setShowVpsLevels] = useState(false);
    const [showBuildings, setShowBuildings] = useState(false);
    const [itinerary, setItinerary] = useState<ItineraryModule | null>(null);
    const [stepsExpanded, setStepsExpanded] = useState(false);
    const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(null);
    const stepItemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const multiRoutersEndpoint = environment === 'prod' ? MULTIROUTERS_ENDPOINT : MULTIROUTERS_ENDPOINT_DEV;

    const livemapRef = useRef<WemapMap>();
    const markers = useRef<{ start: Marker | null; end: Marker | null }>({ start: null, end: null });
    const graphHandlerRef = useRef<GraphHandler>();
    const stepsHandlerRef = useRef<StepsHandler>();
    const userPositionShownRef = useRef(false);
    const userPositionRef = useRef<UserPositionHandler>();
    const itineraryHandlerRef = useRef<ItineraryHandler>();
    const pinpointsHandlerRef = useRef<PinpointsHandler>();
    const vpsHandlerRef = useRef<VpsHandler>();
    const buildingsHandlerRef = useRef<BuildingsHandler>();
    const mapRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<Popup>();
    

    useEffect(() => {
        if (!mapRef.current) {
            return;
        }

        let cancelled = false;

        initializeMap(mapRef.current, String(emmid), token).then((res) => {
            if (cancelled) {
                res.wemapMap.remove();
                return;
            }

            livemapRef.current = res.wemapMap;

            const mapManagerInstance = new MapManager(res.wemapMap);
            setMapManager(mapManagerInstance);

            if (graph) {
                graphHandlerRef.current = new GraphHandler(
                    res.wemapMap,
                    { environment }
                );
            }

            itineraryHandlerRef.current = new ItineraryHandler(
                res.wemapMap,
                res.emmid,
                { environment }
            );

            stepsHandlerRef.current = new StepsHandler(res.wemapMap);
            stepsHandlerRef.current.onStepClick = (stepIndex: number) => {
                setSelectedStepIndex(stepIndex);
                setStepsExpanded(true);
            };

            pinpointsHandlerRef.current = new PinpointsHandler(
                res.wemapMap,
                res.emmid
            );

            pinpointsHandlerRef.current.fetchAllPinpoints().then((pinpoints) => {
                if (itineraryHandlerRef.current) {
                    itineraryHandlerRef.current.allPinpoints = pinpoints;
                }

            });

            userPositionRef.current = new UserPositionHandler(res.wemapMap);
            userPositionRef.current.onStepInstructionChange
                = (proposedInstruction: any, currentInstruction: any) =>
                    // @ts-expect-error TS(2345): Argument of type '{ proposedInstruction: any; curr... Remove this comment to see the full error message
                    setUserStepInstruction({ proposedInstruction, currentInstruction });

            if (graph && graphHandlerRef.current) {
                graphHandlerRef.current.init(graph, useGraphManagement).then(() => {
                    if (pinpointsHandlerRef.current && graphHandlerRef.current && graphHandlerRef.current.graph) {
                        pinpointsHandlerRef.current.setGraph(graphHandlerRef.current.graph);
                    }
                });
            }

            if (vpsMap) {
                vpsHandlerRef.current = new VpsHandler(
                    res.wemapMap,
                    vpsMap
                );
                vpsHandlerRef.current.init();
            }

            buildingsHandlerRef.current = new BuildingsHandler(
                res.wemapMap,
                res.emmid
            );
            buildingsHandlerRef.current.init();

            setMapReady(true);
        }).catch((error) => {
            console.error('Failed to initialize map:', error);
            toast.error(`Failed to load map: ${error.message}`);
        });

        return () => {
            cancelled = true;
            markers.current.start?.remove();
            markers.current.end?.remove();
            markers.current = { start: null, end: null };
            livemapRef.current?.remove();
            livemapRef.current = undefined;
            graphHandlerRef.current = undefined;
            stepsHandlerRef.current = undefined;
            userPositionRef.current = undefined;
            itineraryHandlerRef.current = undefined;
            pinpointsHandlerRef.current = undefined;
            vpsHandlerRef.current = undefined;
            buildingsHandlerRef.current = undefined;
        };
    }, []);

    // Fetch available rules from API
    useEffect(() => {
        if (graph && multiRoutersEndpoint) {
            fetch(`${multiRoutersEndpoint}/rules/${graph}/names`)
                .then(res => res.json())
                .then((data: { names: string[] }) => {
                    setAvailableRules(data.names || []);
                })
                .catch(error => {
                    console.error('Error fetching rules:', error);
                    setAvailableRules([]);
                });
        }
    }, [graph, multiRoutersEndpoint]);

    const moveMarker = useCallback((markerName: 'start'|'end', pos: LatLngLevel | null) => {
        const map = livemapRef.current?.maplibre;

        if (!map) {
            return;
        }

        if (!pos) {
            markers.current[markerName]?.remove();
            markers.current[markerName] = null;
            return;
        }

        if (!markers.current[markerName]) {
            markers.current[markerName] = new maplibregl.Marker({
                color: markersColor[markerName],
            })
                .setLngLat([pos.lng, pos.lat])
                .addTo(map);
        } else {
            markers.current[markerName].setLngLat([pos.lng, pos.lat]);
        }
    }, []);

    useEffect(() => {
        if (!mapReady) {
            return;
        }

        const currentUrl = new URL(window.location.href);

        const from = currentUrl.searchParams.has('from') ? parseLatLngLevelParam(currentUrl.searchParams.get('from')!) : null;
        const to = currentUrl.searchParams.has('to') ? parseLatLngLevelParam(currentUrl.searchParams.get('to')!) : null;
        const rulesParam = currentUrl.searchParams.get('rules');
        const rules = rulesParam ? JSON.parse(rulesParam) : [];
        const travelModeParam = currentUrl.searchParams.get('travelMode');

        if (rules && Array.isArray(rules)) {
            setSelectedRules(rules);
        }

        if (travelModeParam === 'WALK' || travelModeParam === 'BIKE' || travelModeParam === 'CAR' || travelModeParam === 'TRANSIT') {
            setTravelMode(travelModeParam);
        }

        if (from && to) {
            moveMarker('start', from);
            setStart(from);
            moveMarker('end', to);
            setEnd(to);
        }
    }, [mapReady, moveMarker]);

    useEffect(() => {
        const wemapMap = livemapRef.current;

        if (!mapReady || !wemapMap) {
            return () => {};
        }

        const map = wemapMap.maplibre;

        map.getCanvasContainer().style.cursor = 'pointer';

        const onMapClick = (e: any) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: ['step', 'step-2']
            });

            if (features.length) {
                return;
            }

            const currentLevel = getMapLevel(wemapMap);
            const selectedPos: LatLngLevel = {
                lat: e.lngLat.lat,
                lng: e.lngLat.lng,
                level: currentLevel,
            };

            const onStartClick = () => {
                popupRef.current?.remove();
                moveMarker('start', selectedPos);
                setStart(selectedPos);
            };

            const onStartOutdoorClick = () => {
                popupRef.current?.remove();
                const outdoorPos = { ...selectedPos, level: null };
                moveMarker('start', outdoorPos);
                setStart(outdoorPos);
            };

            const onEndClick = () => {
                popupRef.current?.remove();
                moveMarker('end', selectedPos);
                setEnd(selectedPos);
            };

            const onEndOutdoorClick = () => {
                popupRef.current?.remove();
                const outdoorPos = { ...selectedPos, level: null };
                moveMarker('end', outdoorPos);
                setEnd(outdoorPos);
            };

            const onUserPositionClick = () => {
                popupRef.current?.remove();
                userPositionShownRef.current = true;
                userPositionRef.current?.init({
                    lat: e.lngLat.lat,
                    lng: e.lngLat.lng,
                    level: currentLevel,
                });
            };

            const onStartMultipleItineraries = () => {
                popupRef.current?.remove();
                if (itineraryHandlerRef.current) {
                    toast.promise(itineraryHandlerRef.current.checkMultipleItineraries({
                        lat: e.lngLat.lat,
                        lng: e.lngLat.lng,
                        level: getMapLevel(wemapMap)
                    }).then((wrongPinpointsId) => {
                        if (wrongPinpointsId?.length) {
                            throw new Error(`Some itineraries are invalid: ${wrongPinpointsId.join(', ')}`)
                        }
                    }), {
                        loading: `Checking itineraries on ${itineraryHandlerRef.current.allPinpoints.length} pinpoints`,
                        success: 'All itineraries are valid',
                        error: (error) => error.message
                    });
                }
            };

            const comp = (
                <MapPopupContents
                    level={getMapLevel(wemapMap)}
                    onStartOutdoorClick={onStartOutdoorClick}
                    onStartClick={onStartClick}
                    onEndClick={onEndClick}
                    onEndOutdoorClick={onEndOutdoorClick}
                    onStartMultipleItineraries={onStartMultipleItineraries}
                    onUserPositionClick={onUserPositionClick}
                    askUserPosition={!userPositionShownRef.current} />
            );
            const div = document.createElement('div');
            flushSync(() => {
                createRoot(div).render(comp);
            });
            popupRef.current?.remove();
            popupRef.current = new maplibregl.Popup()
                .setLngLat(e.lngLat)
                .setDOMContent(div)
                .addTo(wemapMap.maplibre);
        };

        map.on('click', onMapClick);

        return () => {
            map.off('click', onMapClick);
            popupRef.current?.remove();
        };
    }, [mapReady, moveMarker, selectedRules]);

    useEffect(() => {
        if (selectedStepIndex !== null && stepItemRefs.current[selectedStepIndex]) {
            stepItemRefs.current[selectedStepIndex]?.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest' 
            });
        }
        if (stepsHandlerRef.current) {
            stepsHandlerRef.current.selectedStepIndex = selectedStepIndex;
        }
    }, [selectedStepIndex, stepsExpanded]);

    const updateUrlParams = useCallback((updates: { from?: LatLngLevel | null; to?: LatLngLevel | null; rules?: string[]; travelMode?: TravelMode }) => {
        const newUrl = new URL(window.location.href);
        if (updates.from !== undefined) {
            if (updates.from) newUrl.searchParams.set('from', JSON.stringify(updates.from));
            else newUrl.searchParams.delete('from');
        }
        if (updates.to !== undefined) {
            if (updates.to) newUrl.searchParams.set('to', JSON.stringify(updates.to));
            else newUrl.searchParams.delete('to');
        }
        if (updates.rules !== undefined) {
            if (updates.rules && updates.rules.length > 0) {
                newUrl.searchParams.set('rules', JSON.stringify(updates.rules));
            } else {
                newUrl.searchParams.delete('rules');
            }
        }
        if (updates.travelMode !== undefined) {
            if (updates.travelMode !== 'WALK') {
                newUrl.searchParams.set('travelMode', updates.travelMode);
            } else {
                newUrl.searchParams.delete('travelMode');
            }
        }
        history.replaceState({}, '', newUrl);
    }, []);

    useEffect(() => {
        if (!mapReady) return;

        let cancelled = false;

        if (start && end) {
            updateUrlParams({ from: start, to: end, rules: selectedRules, travelMode });
            computeItinerary(emmid, start, end, { itineraryRules: selectedRules, multiRoutersEndpoint, travelMode })
                .then((itineraryComputed) => {
                    if (cancelled) return;

                    setItinerary(itineraryComputed);
                    setSelectedStepIndex(null);
                    stepsHandlerRef.current && (stepsHandlerRef.current.itinerary = itineraryComputed);
                    userPositionRef.current && (userPositionRef.current.itinerary = itineraryComputed);
                    itineraryHandlerRef.current && (itineraryHandlerRef.current.itinerary = itineraryComputed);
                })
                .catch((error) => {
                    if (cancelled) return;
                    console.error('Failed to compute itinerary:', error);
                    toast.error('Failed to compute itinerary');
                });
        } else {
            updateUrlParams({ rules: selectedRules, travelMode });
        }

        return () => {
            cancelled = true;
        };
    }, [start, end, selectedRules, travelMode, mapReady, emmid, updateUrlParams, multiRoutersEndpoint]);

    const updateMarkersVisibility = useCallback((mapLevel: number | null | undefined) => {
        const shouldShow = (pos: LatLngLevel | null) =>
            pos && (mapLevel == null || pos.level == null || pos.level === mapLevel);

        moveMarker('start', shouldShow(start) ? start : null);
        moveMarker('end', shouldShow(end) ? end : null);
    }, [start, end, moveMarker]);

    useEffect(() => {
        const wemapMap = livemapRef.current;

        if (!wemapMap) {
            return;
        }

        const unsubscribe = wemapMap.onLevelChange((level) => {
            updateMarkersVisibility(level);
        });

        updateMarkersVisibility(wemapMap.getLevel());

        return unsubscribe;
    }, [updateMarkersVisibility]);

    // Update markers when start/end change
    useEffect(() => {
        if (!mapReady) return;

        updateMarkersVisibility(livemapRef.current?.getLevel());
    }, [start, end, mapReady, updateMarkersVisibility]);

    let userStepInstructionContainer = null;

    if (userStepInstruction) {
        const { proposedInstruction } = userStepInstruction;
        userStepInstructionContainer = (
            <div id="userposition-step-container">
                {proposedInstruction}
            </div>
        );
    }

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const geojson = JSON.parse((e.target?.result as string) || '');
                mapManager?.addGeoJSON?.(geojson);
                mapManager?.updateFeatures(getMapLevel(livemapRef.current) ?? 0);
            } catch (err) {
                console.error('Failed to parse GeoJSON file:', err);
            }
        };
        reader.readAsText(file);
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    };

    const handleShowPinpointsProjection = (show: boolean) => {
        setShowPinpointsProjection(show);

        if (pinpointsHandlerRef.current) {
            if (show) {
                pinpointsHandlerRef.current.showProjectionFeatures();
            } else {
                pinpointsHandlerRef.current.hideProjectionFeatures();
            }
        }
    };

    const handleShowGraph = (show: boolean) => {
        setShowGraph(show);

        if (graphHandlerRef.current) {
            if (show) {
                graphHandlerRef.current.showGraph();
            } else {
                graphHandlerRef.current.hideGraph();
            }
        }
    };

    const handleShowItinerary = (show: boolean) => {
        setShowItinerary(show);

        if (itineraryHandlerRef.current) {
            if (show) {
                itineraryHandlerRef.current.showItinerary();
            } else {
                itineraryHandlerRef.current.hideItinerary();
            }
        }

        if (stepsHandlerRef.current) {
            if (show) {
                stepsHandlerRef.current.showSteps();
            } else {
                stepsHandlerRef.current.hideSteps();
            }
        }
    };

    const handleShowVpsLevels = (show: boolean) => {
        setShowVpsLevels(show);

        if (vpsHandlerRef.current) {
            if (show) {
                vpsHandlerRef.current.showLevels();
            } else {
                vpsHandlerRef.current.hideLevels();
            }
        }
    };

    const handleShowBuildings = (show: boolean) => {
        setShowBuildings(show);

        if (buildingsHandlerRef.current) {
            if (show) {
                buildingsHandlerRef.current.showBuildings();
            } else {
                buildingsHandlerRef.current.hideBuildings();
            }
        }
    };

    const handleStepClick = useCallback((step: any, index: number) => {
        const wemapMap = livemapRef.current;

        if (!wemapMap) return;

        setSelectedStepIndex(index);

        wemapMap.flyTo({
            center: [step.coords.lng, step.coords.lat],
            zoom: wemapMap.getZoom(),
            duration: 500
        });

        if (step.coords.level !== null) {
            wemapMap.setLevel(step.coords.level);
        }
    }, []);

    const handleCloseItinerary = useCallback(() => {
        setItinerary(null);
        setStart(null);
        setEnd(null);
        setStepsExpanded(false);
        setSelectedStepIndex(null);

        moveMarker('start', null);
        moveMarker('end', null);

        itineraryHandlerRef.current && (itineraryHandlerRef.current.itinerary = undefined);
        stepsHandlerRef.current && (
            stepsHandlerRef.current.itinerary = null,
            stepsHandlerRef.current.selectedStepIndex = null
        );

        updateUrlParams({ from: null, to: null, rules: [] });
    }, [moveMarker, updateUrlParams]);

    return (
        <>
            <div onDrop={handleDrop} onDragOver={handleDragOver} style={{ width: '100%', height: '100%' }}>
                {userStepInstructionContainer}

                <Accordion
                    type="single" collapsible css={{
                    position: 'absolute',
                    bottom: '2',
                    left: '2',
                    zIndex: '9',
                    rounded: 'lg',
                    width: '280px',
                }}>
                    <AccordionItem
                        value="options"
                        trigger="Legend"
                        maxHeight="600px">
                        <VStack alignItems="flex-start" gap="0px">
                            <CheckboxLabel>
                                <Checkbox 
                                    type="checkbox"
                                    checked={showGraph}
                                    onChange={(e) => handleShowGraph(e.target.checked)}
                                    style={{ accentColor: '#f44336' }}
                                />
                                <OptionText style={{ color: '#f44336' }}>Show graph info</OptionText>
                            </CheckboxLabel>
                            {showGraph && (
                                <LegendContainer>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: '#f44336' }} />
                                        <LegendText>Indoor edges</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: '#ff9800' }} />
                                        <LegendText>Outdoor edges</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <img src="/img/arrow.svg" alt="arrow" style={{ width: 14, height: 14, marginLeft: 2, marginRight: 2, verticalAlign: 'middle' }} />
                                        <LegendText>One-way edges</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: '#f44336', borderBottom: '2px dashed #f44336', background: 'none' }} />
                                        <LegendText>Multilevel edges</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: '#1a0000' }} />
                                        <LegendText>Disabled edges</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(wheelchairDesignatedSvg)}`} alt="wheelchair designated" style={{ width: 16, height: 16, verticalAlign: 'middle' }} />
                                        <LegendText>Wheelchair designated</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(wheelchairNoSvg)}`} alt="wheelchair no" style={{ width: 16, height: 16, verticalAlign: 'middle' }} />
                                        <LegendText>Wheelchair prohibited</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: 'transparent', borderStyle: 'dashed', borderWidth: '2px', borderColor: '#b71c1c', height: '12px' }} />
                                        <LegendText>Routing bounds
                                            <InfoIcon>
                                                i
                                                <span className="tooltip-text">Outside of this limit, the OSRM router is used.</span>
                                            </InfoIcon>
                                        </LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: '#b71c1c', borderRadius: '50%', width: '8px', height: '8px' }} />
                                        <LegendText>Routing I/O points</LegendText>
                                    </LegendItem>
                                </LegendContainer>
                            )}
                            <CheckboxLabel>
                                <Checkbox 
                                    type="checkbox"
                                    checked={showItinerary}
                                    onChange={(e) => handleShowItinerary(e.target.checked)}
                                    disabled={!itinerary}
                                    style={{ accentColor: '#45b4dd' }}
                                />
                                <OptionText style={{ 
                                    opacity: itinerary ? 1 : 0.5,
                                    color: '#45b4dd'
                                }}>
                                    Show itinerary info
                                </OptionText>
                            </CheckboxLabel>
                            {showItinerary && itinerary && (
                                <LegendContainer>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: '#45b4dd' }} />
                                        <LegendText>Itinerary</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#2980b9', backgroundColor: 'transparent' }} />
                                        <LegendText>Projections</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <div style={{ position: 'relative', width: '16px', height: '16px' }}>
                                            <div style={{ 
                                                position: 'absolute', 
                                                width: '16px', 
                                                height: '16px', 
                                                backgroundColor: '#0000ff', 
                                                borderRadius: '50%',
                                                top: '0',
                                                left: '0'
                                            }} />
                                            <div style={{ 
                                                position: 'absolute', 
                                                width: '10px', 
                                                height: '10px', 
                                                backgroundColor: '#ff0000', 
                                                borderRadius: '50%',
                                                top: '3px',
                                                left: '3px'
                                            }} />
                                        </div>
                                        <LegendText>Steps</LegendText>
                                    </LegendItem>
                                </LegendContainer>
                            )}
                            <CheckboxLabel>
                                <Checkbox 
                                    type="checkbox"
                                    checked={showPinpointsProjection}
                                    onChange={(e) => handleShowPinpointsProjection(e.target.checked)}
                                    style={{ accentColor: '#8B5CF6' }}
                                />
                                <OptionText style={{ color: '#8B5CF6' }}>Show PoIs info</OptionText>
                            </CheckboxLabel>
                            {showPinpointsProjection && (
                                <LegendContainer>
                                    <LegendItem>
                                        <LegendColor style={{ borderStyle: 'dashed', borderWidth: '2px', borderColor: '#8B5CF6', backgroundColor: 'transparent' }} />
                                        <LegendText>PoIs projections
                                            <InfoIcon>
                                                i
                                                <span className="tooltip-text">Limit: The projection of the outdoor PoIs does not use OSRM graph un this tool but use it in the apps.</span>
                                            </InfoIcon>
                                        </LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: '#4C1D95', borderRadius: '50%', width: '8px', height: '8px' }} />
                                        <LegendText>Indoor PoIs</LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: '#C084FC', borderRadius: '50%', width: '8px', height: '8px' }} />
                                        <LegendText>Outdoor PoIs</LegendText>
                                    </LegendItem>
                                </LegendContainer>
                            )}
                            <CheckboxLabel>
                                <Checkbox 
                                    type="checkbox"
                                    checked={showBuildings}
                                    onChange={(e) => handleShowBuildings(e.target.checked)}
                                    style={{ accentColor: '#FFD700' }}
                                />
                                <OptionText style={{ color: '#FFD700' }}>Show Building API info</OptionText>
                            </CheckboxLabel>
                            {showBuildings && (
                                <LegendContainer>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: 'transparent', borderStyle: 'solid', borderWidth: '2px', borderColor: '#DAA520', height: '12px' }} />
                                        <LegendText>Building outline
                                            <InfoIcon>
                                                i
                                                <span className="tooltip-text">The building outline from Wemap Backend. It's not the building from OSM.</span>
                                            </InfoIcon>
                                        </LegendText>
                                    </LegendItem>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: 'transparent', borderStyle: 'dashed', borderWidth: '2px', borderColor: '#FFC107', height: '12px' }} />
                                        <LegendText>Level outline
                                            <InfoIcon>
                                                i
                                                <span className="tooltip-text">Individual floor/level geometries within buildings from Wemap Backend.</span>
                                            </InfoIcon>
                                        </LegendText>
                                    </LegendItem>
                                </LegendContainer>
                            )}
                            {vpsMap && (
                                <CheckboxLabel>
                                    <Checkbox 
                                        type="checkbox"
                                        checked={showVpsLevels}
                                        onChange={(e) => handleShowVpsLevels(e.target.checked)}
                                        style={{ accentColor: '#22C55E' }}
                                    />
                                    <OptionText style={{ color: '#22C55E' }}>Show VPS info</OptionText>
                                </CheckboxLabel>
                            )}
                            {vpsMap && showVpsLevels && (
                                <LegendContainer>
                                    <LegendItem>
                                        <LegendColor style={{ backgroundColor: 'transparent', borderStyle: 'solid', borderWidth: '2px', borderColor: '#22C55E', height: '12px' }} />
                                        <LegendText>
                                            VPS level geometry
                                            <InfoIcon>
                                                i
                                                <span className="tooltip-text">outside of the geometry is considered as outdoor</span>
                                            </InfoIcon>
                                        </LegendText>
                                    </LegendItem>
                                </LegendContainer>
                            )}
                        </VStack>
                    </AccordionItem>
                </Accordion>
                <LevelSwitcher wemapMap={mapReady ? livemapRef.current : null} />
                <div id="map"
                    ref={mapRef} />
                <RoutePanel>
                    <RouteSection>
                        <RouteInputRow>
                            <RouteMarkerIcon dangerouslySetInnerHTML={{ __html: greenMarkerSvg }} />
                            <RouteInputLabel>From:</RouteInputLabel>
                            <RouteInput type="text" value={formatCoordinate(start)} readOnly />
                        </RouteInputRow>
                        <RouteInputRow>
                            <RouteMarkerIcon dangerouslySetInnerHTML={{ __html: redMarkerSvg }} />
                            <RouteInputLabel>To:</RouteInputLabel>
                            <RouteInput type="text" value={formatCoordinate(end)} readOnly />
                        </RouteInputRow>
                        <RouteInputRow>
                            <RouteMarkerIcon />
                            <RouteInputLabel>Mode:</RouteInputLabel>
                            <RulesDropdown
                                value={travelMode}
                                onChange={(e) => setTravelMode(e.target.value as TravelMode)}
                            >
                                <option value="WALK">Walk</option>
                                <option value="BIKE">Bike</option>
                                <option value="CAR">Car</option>
                                <option value="TRANSIT">Transit</option>
                            </RulesDropdown>
                        </RouteInputRow>
                        <RulesContainer>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                <CheckboxLabel>
                                    <Checkbox 
                                        type="checkbox"
                                        checked={selectedRules.includes('wheelchair')}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                if (!selectedRules.includes('wheelchair')) {
                                                    setSelectedRules([...selectedRules, 'wheelchair']);
                                                }
                                            } else {
                                                setSelectedRules(selectedRules.filter(r => r !== 'wheelchair'));
                                            }
                                        }}
                                        style={{ accentColor: '#1976d2' }}
                                    />
                                    <img 
                                        src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(wheelchairDesignatedSvg)}`} 
                                        alt="wheelchair" 
                                        style={{ width: '16px', height: '16px' }} 
                                    />
                                    <OptionText style={{ color: '#1976d2' }}>Wheelchair</OptionText>
                                </CheckboxLabel>
                                <CheckboxLabel>
                                    <Checkbox 
                                        type="checkbox"
                                        disabled
                                        style={{ accentColor: '#1976d2', opacity: 0.5 }}
                                    />
                                    <img 
                                        src={visuallyImpairedIcon} 
                                        alt="visually impaired" 
                                        style={{ width: '16px', height: '16px' }} 
                                    />
                                    <OptionText style={{ color: '#1976d2', opacity: 0.5 }}>Visually Impaired</OptionText>
                                </CheckboxLabel>
                            </div>
                            {availableRules.filter(rule => !FILTERED_RULES.includes(rule as any)).length > 0 && (
                                <RulesDropdown
                                    value=""
                                    onChange={(e) => {
                                        const rule = e.target.value;
                                        if (rule && !selectedRules.includes(rule)) {
                                            setSelectedRules([...selectedRules, rule]);
                                        }
                                        e.target.value = '';
                                    }}
                                >
                                    <option value="">Select a custom rule...</option>
                                    {availableRules
                                        .filter(rule => !FILTERED_RULES.includes(rule as any) && !selectedRules.includes(rule))
                                        .map(rule => (
                                            <option key={rule} value={rule}>
                                                {rule}
                                            </option>
                                        ))}
                                </RulesDropdown>
                            )}
                            {selectedRules.filter(rule => !FILTERED_RULES.includes(rule as any)).length > 0 && (
                                <RulesTagsContainer>
                                    {selectedRules
                                        .filter(rule => !FILTERED_RULES.includes(rule as any))
                                        .map(rule => (
                                            <RuleTag key={rule}>
                                                <span>{rule}</span>
                                                <RuleTagRemove
                                                    className="rule-tag-remove"
                                                    onClick={() => {
                                                        setSelectedRules(selectedRules.filter(r => r !== rule));
                                                    }}
                                                >
                                                    ×
                                                </RuleTagRemove>
                                            </RuleTag>
                                        ))}
                                </RulesTagsContainer>
                            )}
                        </RulesContainer>
                    </RouteSection>
                </RoutePanel>
                {itinerary && (itinerary.distance !== undefined || itinerary.duration !== undefined) && (
                    <InfoPanel>
                        <PanelHeader>
                            <HeaderInfo>
                                <MainInfo>
                                    {itinerary.distance !== undefined && (
                                        <DistanceText>
                                            <IconWrapper>
                                                <div dangerouslySetInnerHTML={{ __html: distanceSvg }} />
                                            </IconWrapper>
                                            {formatDistance(itinerary.distance)}
                                        </DistanceText>
                                    )}
                                    {itinerary.duration !== undefined && (
                                        <TimeText>
                                            <IconWrapper>
                                                <div dangerouslySetInnerHTML={{ __html: durationSvg }} />
                                            </IconWrapper>
                                            {formatTime(itinerary.duration)}
                                        </TimeText>
                                    )}
                                </MainInfo>
                            </HeaderInfo>
                            <CloseButton onClick={handleCloseItinerary}>
                                <svg viewBox="0 0 24 24">
                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                </svg>
                            </CloseButton>
                        </PanelHeader>
                        {itinerary.steps && itinerary.steps.length > 0 && (
                            <StepsSection>
                                <StepsToggle onClick={() => setStepsExpanded(!stepsExpanded)}>
                                    <StepsCount style={{ display: 'flex', alignItems: 'center' }}>
                                        <IconWrapper style={{ marginRight: '6px' }}>
                                            <div dangerouslySetInnerHTML={{ __html: roadbookSvg }} />
                                        </IconWrapper>
                                        {itinerary.steps.length} steps
                                    </StepsCount>
                                    <ExpandIcon className={stepsExpanded ? 'expanded' : ''}>
                                        <svg viewBox="0 0 24 24">
                                            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                                        </svg>
                                    </ExpandIcon>
                                </StepsToggle>
                                {stepsExpanded && (
                                    <StepsList>
                                        {itinerary.steps.map((step, index: number) => {
                                            const instruction = InstructionManager.getInstructionFromStep(step);
                                            const nextStep = itinerary.steps[index + 1];
                                            const isLastStep = index === itinerary.steps.length - 1;
                                            const distanceFromCoords = nextStep 
                                                ? step.coords.distanceTo(nextStep.coords)
                                                : null;
                                            const distanceFromStep = step.distance || null;
                                            const durationFromStep = step.duration || null;
                                            
                                            // Debug: compare both values
                                            if (distanceFromCoords !== null && distanceFromStep !== null) {
                                                console.log(`Step ${index + 1}: distanceFromCoords=${distanceFromCoords}, distanceFromStep=${distanceFromStep}, diff=${Math.abs(distanceFromCoords - distanceFromStep)}`);
                                            }
                                            
                                            const distanceToNext = distanceFromStep;
                                            const durationToNext = durationFromStep;
                                            const iconSvg = isLastStep ? redMarkerSvg : getInstructionIcon(step);
                                            const isLevelChange = typeof step.levelDifference === 'number' && step.levelDifference !== 0;
                                            
                                            return (
                                                <div key={index}>
                                                    <StepItem 
                                                        className={selectedStepIndex === index ? 'selected' : ''}
                                                        ref={(el) => {
                                                            stepItemRefs.current[index] = el;
                                                        }}
                                                        onClick={() => handleStepClick(step, index)}
                                                    >
                                                        <StepInstruction>
                                                            {step.important && <span style={{ fontWeight: 'bold' }}>{index + 1}. {instruction}</span>}
                                                            {!step.important && <span>{index + 1}. {instruction}</span>}
                                                            
                                                            {iconSvg && (
                                                                <StepIcon 
                                                                    className={isLastStep ? 'destination' : ''}
                                                                    dangerouslySetInnerHTML={{ __html: iconSvg }} 
                                                                />
                                                            )}
                                                        </StepInstruction>
                                                    </StepItem>
                                                    {(distanceToNext !== null || durationToNext !== null) && !isLastStep && (
                                                        <StepSeparatorContainer isLevelChange={isLevelChange}>
                                                            {(() => {
                                                                return (
                                                                    <>
                                                                        <StepSeparator isLevelChange={isLevelChange}>
                                                                            <StepSeparatorText>
                                                                                {distanceToNext !== null && formatDistance(distanceToNext)}
                                                                                {distanceToNext !== null && durationToNext !== null && ' • '}
                                                                                {durationToNext !== null && formatTime(durationToNext)}
                                                                            </StepSeparatorText>
                                                                        </StepSeparator>
                                                                        {isLevelChange && nextStep && nextStep.coords?.level !== undefined && (
                                                                            <LevelLabel>Level {nextStep.coords.level}</LevelLabel>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </StepSeparatorContainer>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </StepsList>
                                )}
                            </StepsSection>
                        )}
                    </InfoPanel>
                )}
            </div>

        </>
    );
}
Itinerary.propTypes = {
    emmid: PropTypes.number,
    token: PropTypes.string,
    graph: PropTypes.string,
    vpsMap: PropTypes.string,
    useGraphManagement: PropTypes.bool,
    environment: PropTypes.string
};
